import React, { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BadgeCheckIcon, BellIcon, BriefcaseBusinessIcon, CompassIcon, CrownIcon, FileBadgeIcon, GraduationCapIcon, HouseIcon, LayoutDashboardIcon, MessageCircleIcon, MoreHorizontalIcon, SettingsIcon, SparklesIcon, StickyNoteIcon, StoreIcon, SunIcon, UserCircleIcon, WalletIcon, XIcon, BoxIcon } from "lucide-react";
import { Logo } from "./Logo";
import { GlobalSearch } from "./GlobalSearch";
import { NotificationBell } from "./NotificationBell";
import { ConsoleSwitcher } from "./ConsoleSwitcher";
import { AssistantDock } from "../intelligence/AssistantDock";
import { QuickCapture } from "../notes/QuickCapture";
import { DataModeBanner } from "../platform/DataModeBanner";
import { useKeyboardOpen } from "../../hooks/useKeyboardOpen";
type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  end?: boolean;
};

/**
 * Sidebar navigation, grouped by the platform's own architecture rather than
 * presented as one flat list. Every entry is a real destination — nothing here
 * leads to a page that does not exist.
 */
const GROUPS: {
  label: string;
  items: NavItem[];
}[] = [{
  label: 'Main',
  items: [{
    to: '/',
    label: 'Home',
    icon: LayoutDashboardIcon,
    end: true
  }, {
    to: '/academics',
    label: 'Academics',
    icon: GraduationCapIcon
  }, {
    to: '/housing',
    label: 'Housing',
    icon: HouseIcon
  }, {
    to: '/market',
    label: 'Marketplace',
    icon: StoreIcon
  }, {
    to: '/social',
    label: 'Campus',
    icon: SparklesIcon
  }, {
    to: '/money',
    label: 'Money',
    icon: WalletIcon
  }]
}, {
  label: 'Communication',
  items: [{
    to: '/community',
    label: 'Messages',
    icon: MessageCircleIcon
  }, {
    to: '/notifications',
    label: 'Notifications',
    icon: BellIcon
  }]
}, {
  label: 'Productivity',
  items: [{
    to: '/notes',
    label: 'Notes',
    icon: StickyNoteIcon
  }, {
    to: '/services',
    label: 'Campus services',
    icon: FileBadgeIcon
  }, {
    to: '/discover',
    label: 'Discover',
    icon: CompassIcon
  }, {
    to: '/opportunities',
    label: 'Opportunities',
    icon: BriefcaseBusinessIcon
  }, {
    to: '/marketplace',
    label: 'Knowledge & orders',
    icon: StoreIcon
  }]
}, {
  label: 'You',
  items: [{
    to: '/profile',
    label: 'Profile',
    icon: UserCircleIcon
  }, {
    to: '/identity',
    label: 'Academic identity',
    icon: BadgeCheckIcon
  }]
}, {
  label: 'System',
  items: [{
    to: '/settings',
    label: 'Settings',
    icon: SettingsIcon
  }, {
    to: '/support',
    label: 'Help & support',
    icon: BoxIcon
  }]
}];

/**
 * Five bottom-bar destinations, chosen by what a student opens the phone for:
 * their day, what is happening around them, buying something, replying to
 * someone, and everything else.
 */
const MOBILE_PRIMARY: NavItem[] = [{
  to: '/',
  label: 'Home',
  icon: LayoutDashboardIcon,
  end: true
}, {
  to: '/social',
  label: 'Campus',
  icon: SparklesIcon
}, {
  to: '/market',
  label: 'Market',
  icon: StoreIcon
}, {
  to: '/community',
  label: 'Messages',
  icon: MessageCircleIcon
}];
const navItemClass = ({
  isActive
}: {
  isActive: boolean;
}) => `flex min-h-touch items-center gap-3 rounded-xl px-3 text-body-sm transition-colors duration-150 ease-out ${isActive ? 'bg-brand-50 font-semibold text-brand-800' : 'font-medium text-ink-muted hover:bg-canvas hover:text-ink'}`;
function SidebarNav({
  onNavigate
}: {
  onNavigate?: () => void;
}) {
  return <nav aria-label="Primary" className="flex flex-col gap-5 px-3 pb-6" data-id="element-4656" data-mp="31hbv9ll7dzh4">
      {GROUPS.map((group, index) => <div key={group.label} className={index > 0 ? 'border-t border-line pt-5' : undefined} data-id="element-4657" data-mp="36aen9pac10v7" data-mpi={index}>
          {index > 0 && <h2 className="mb-2 px-3 text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4658" data-mp="29qp96as0i60l">
              {group.label}
            </h2>}
          <ul className="space-y-1" data-id="element-4659" data-mp="1unec2jw7r8es">
            {group.items.map(item => {
          const Icon = item.icon;
          return <li key={item.to} data-id="element-4660" data-mp="1ppvygg79450g">
                  <NavLink to={item.to} end={item.end} onClick={onNavigate} className={navItemClass} data-id="element-4661" data-mp="1pmqaj4acz96b">
                    {({
                isActive
              }) => <>
                        <Icon aria-hidden className={`h-5 w-5 shrink-0 ${isActive ? 'text-brand-700' : 'text-ink-subtle'}`} data-id="element-4662" data-mp="1zlfud2ltf3p7" />
                        <span className="truncate" data-id="element-4663" data-mp="2dgi5c45irj8v">{item.label}</span>
                      </>}
                  </NavLink>
                </li>;
        })}
          </ul>
        </div>)}
    </nav>;
}

/** Appearance is honest about what exists: light only, for now. */
function AppearanceControl() {
  return <div className="px-6" data-id="element-4664" data-mp="2ibfdz9yvzql8">
      <p className="flex min-h-touch items-center gap-2 rounded-xl border border-line px-3 text-body-sm font-medium text-ink" data-id="element-4665" data-mp="syak75zfcvs5">
        <SunIcon aria-hidden className="h-4 w-4 text-ink-subtle" data-id="element-4666" data-mp="o0it2e6ltdp0" />
        Light
        <span className="ml-auto text-caption font-normal text-ink-subtle" data-id="element-4667" data-mp="2zmawmu067eae">Only theme</span>
      </p>
    </div>;
}
function PlusCard() {
  return <div className="px-6 pb-6 pt-4" data-id="element-4668" data-mp="34le2uhrvzqjy">
      <div className="rounded-card border border-line bg-brand-50 px-4 py-4" data-id="element-4669" data-mp="2px14je5btvma">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white" data-id="element-4670" data-mp="2pux2z2uwh8p6">
          <CrownIcon aria-hidden className="h-4 w-4 text-warn-700" data-id="element-4671" data-mp="2uu9onka1c9q6" />
        </span>
        <p className="mt-3 text-body-sm font-semibold text-ink" data-id="element-4672" data-mp="2gvjt492v6i3x">Upgrade your experience</p>
        <p className="mt-1 text-caption leading-5 text-ink-muted" data-id="element-4673" data-mp="2kyzol2376cf1">
          Unlock more with Campuzon Plus.
        </p>
        <Link to="/#campuzon-plus" className="mt-3 inline-flex min-h-touch w-full items-center justify-center rounded-lg bg-brand-700 px-3 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800 hover:text-white" data-id="element-4674" data-mp="26zzwtlke1hen">
          Explore plans
        </Link>
      </div>
    </div>;
}
export function StudentShell() {
  const [moreOpen, setMoreOpen] = useState(false);
  /* The bottom bar is anchored to the layout viewport, so while the keyboard is
     open it would sit on top of the field being typed into. It steps aside. */
  const keyboardOpen = useKeyboardOpen();
  return <div className="min-h-full w-full bg-canvas" data-id="element-4675" data-mp="3uy0o2xsez4yr">
      {/* Fixed application sidebar. Desktop only; the sheet carries it below lg. */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[260px] flex-col overflow-y-auto border-r border-line bg-surface lg:flex" data-id="element-4676" data-mp="rexvfsg4ptdo">
        <div className="flex h-16 shrink-0 items-center px-6" data-id="element-4677" data-mp="mhfm40y79c55">
          <Logo data-id="element-4678" data-mp="how9b67c6l1e" />
        </div>
        <div className="flex-1" data-id="element-4679" data-mp="ho3b4tmg13uv">
          <SidebarNav data-id="element-4680" data-mp="hnbhvpgbji7r" />
        </div>
        <div className="shrink-0 border-t border-line pt-4" data-id="element-4681" data-mp="cqus6qth0vsk">
          <AppearanceControl data-id="element-4682" data-mp="co8hwjf7426n" />
          <PlusCard data-id="element-4683" data-mp="1a68mc9luu65k" />
        </div>
      </aside>

      <div className="flex min-h-full flex-col lg:pl-[260px]" data-id="element-4684" data-mp="1a707eqhtslhl">
        <DataModeBanner data-id="element-4685" data-mp="15eqj1ewczthf" />

        <header className="sticky top-0 z-20 border-b border-line bg-surface" data-id="element-4686" data-mp="15dfjjr9y57w4">
          <div className="mx-auto flex h-16 w-full max-w-[1440px] items-center gap-3 px-4 sm:px-6" data-id="element-4687" data-mp="1doo7f8d0ao17">
            <div className="lg:hidden" data-id="element-4688" data-mp="1hq9fmgelie52">
              <Logo data-id="element-4689" data-mp="1hravcy6k3ot4" />
            </div>
            <div className="hidden min-w-0 flex-1 md:flex" data-id="element-4690" data-mp="1mnrpqa4cmzen">
              <GlobalSearch data-id="element-4691" data-mp="1mqnqe67l026s" />
            </div>
            <div className="ml-auto flex items-center gap-2" data-id="element-4692" data-mp="1rnnuibkveuv9">
              <div className="md:hidden" data-id="element-4693" data-mp="1wmh0zk1qpllx">
                <GlobalSearch data-id="element-4694" data-mp="1wlfqphsjt359" />
              </div>
              <QuickCapture data-id="element-4695" data-mp="1wko06ldt5vlu" />
              <AssistantDock data-id="element-4696" data-mp="20nanqa3g4ud8" />
              <NotificationBell data-id="element-4697" data-mp="20mj2nt7h6f17" />
              <ConsoleSwitcher current="student" data-id="element-4698" data-mp="20lptymoyhfgf" />
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1440px] flex-1 px-4 pb-24 pt-6 sm:px-6 lg:pb-12" data-id="element-4699" data-mp="25h57fdj1x9cb">
          <Outlet data-id="element-4700" data-mp="2afq5b6atzgxl" />
        </main>
      </div>

      <nav aria-label="Primary mobile" hidden={keyboardOpen} className={`fixed bottom-0 left-0 right-0 z-30 flex border-t border-line bg-surface lg:hidden ${keyboardOpen ? 'hidden' : ''}`} data-id="element-4701" data-mp="2fe1dzdpe3le8">
        {MOBILE_PRIMARY.map(item => {
        const Icon = item.icon;
        return <NavLink key={item.to} to={item.to} end={item.end} className={({
          isActive
        }) => `flex min-h-touch-lg min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 py-2 text-caption transition-colors duration-150 ease-out ${isActive ? 'font-semibold text-brand-700' : 'font-medium text-ink-muted'}`} data-id="element-4702" data-mp="30o6zt0mt90p0">
              <Icon aria-hidden className="h-5 w-5 shrink-0" data-id="element-4703" data-mp="2m02f5r476f3l" />
              <span className="w-full truncate text-center" data-id="element-4704" data-mp="2hxpaz753mp8x">{item.label}</span>
            </NavLink>;
      })}
        <button type="button" onClick={() => setMoreOpen(true)} aria-label="More sections" className="cz-block flex min-h-touch-lg min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 py-2 text-caption font-medium text-ink-muted" data-id="element-4705" data-mp="2vokee3wtwe54">
          <MoreHorizontalIcon aria-hidden className="h-5 w-5 shrink-0" data-id="element-4706" data-mp="236yirxzlho5a" />
          <span className="w-full truncate text-center" data-id="element-4707" data-mp="1ya82b9jvoqjc">More</span>
        </button>
      </nav>

      <AnimatePresence data-id="element-4708" data-mp="dr5oq9giv8sy">
        {moreOpen && <div className="fixed inset-0 z-40 flex items-end justify-center sm:items-center lg:hidden" data-id="element-4709" data-mp="dq4da3vlgkap">
            <motion.div className="absolute inset-0 bg-ink/40" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15,
          ease: 'easeOut'
        }} onClick={() => setMoreOpen(false)} data-id="element-4710" data-mp="ijfcpjy2tysl" />
            <motion.div role="dialog" aria-label="All sections" className="relative max-h-[86dvh] w-full overflow-y-auto rounded-t-2xl border-t border-line bg-surface pb-8 sm:max-w-lg sm:rounded-card sm:border" initial={{
          y: 24,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 16,
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-4711" data-mp="xg6fm36ylsmy">
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-surface px-5 py-3.5" data-id="element-4712" data-mp="3d7c1vwykd4eb">
                <p className="text-body font-semibold text-ink" data-id="element-4713" data-mp="9rnnyqwwhf0x">All sections</p>
                <button type="button" onClick={() => setMoreOpen(false)} aria-label="Close" className="-mr-2 flex h-11 w-11 items-center justify-center rounded-md text-ink-muted hover:bg-canvas hover:text-ink" data-id="element-4714" data-mp="eo4etsvhi7hp">
                  <XIcon aria-hidden className="h-5 w-5" data-id="element-4715" data-mp="4wzv6fuv5ygj" />
                </button>
              </div>
              <div className="pt-4" data-id="element-4716" data-mp="3n6jzdxdhzgc8">
                <SidebarNav onNavigate={() => setMoreOpen(false)} data-id="element-4717" data-mp="3r6kd1fsrvni8" />
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}